import {useState} from "react";
import {useSelector} from "react-redux";
import {useFormContext} from "react-hook-form";
import {RootState} from "@/store";
import Button from "@/components/common/Button";
import { ReactComponent as RiArrowDownSLine } from '@/assets/icons/ri-arrow-down-s-line.svg';
import { ReactComponent as BadmintonCourt } from '@/assets/icons/badminton-court.svg';
import {currencyFormat, getLongDateFormat, hourFormat} from "@/utils";

const groupInvoiceDetailByDate = (invoiceDetails: InvoiceDetail[]) => {
  const groupedItems = {};

  if (!invoiceDetails) {
    return groupedItems
  }

  invoiceDetails.forEach(item => {
    const itemDate = item.date;
    if (!groupedItems[itemDate]) {
      groupedItems[itemDate] = [];
    }
    groupedItems[itemDate].push(item);
  });

  return groupedItems;
}
const getPriceOfDate = (items: InvoiceDetail[]) => items.reduce((sum, item) => sum + item.price, 0)

const RefundDetails = () => {
  const { watch, getValues, setValue } = useFormContext()
  const invoice = useSelector((rootState: RootState) => rootState.invoice.state.detail)
  const {venues} = useSelector((rootState: RootState) => rootState.userInfo.state)
  const invoiceDetailsGroupedByDate = groupInvoiceDetailByDate(invoice?.invoice_detail)
  const venue = venues.find(v => v.id === invoice?.venue_id)
  const courts = venue?.courts
  const currentRefundDetails = watch('details')
  const [openedTimestamp, setOpenedTimestamp] = useState([])
  const [isRefundTax, setIsRefundTax] = useState(getValues('is_refund_tax'))


  const subTotal = invoice.invoice_detail.reduce((acc, curr) =>
  acc + curr.price, 0)
  const discount = invoice.discount_amount
  const rescheduleFee = invoice.booking?.rescheduled_from != null ? invoice.booking?.rescheduled_from.fee : 0
  const paidFromRechedule = invoice.booking?.rescheduled_from != null ? invoice.booking?.rescheduled_from.paid_amount : 0

  const toggleTimestamp = (timestamp) => {
    if (openedTimestamp.includes(timestamp)) {
      setOpenedTimestamp(openedTimestamp.filter(t => t !== timestamp))
    } else {
      setOpenedTimestamp([...openedTimestamp, timestamp])
    }
  }
  const isOpen = timestamp => openedTimestamp.includes(timestamp)
  const isTimestampSelected = (invDetails: InvoiceDetail[]):boolean => invDetails.every(d => currentRefundDetails.find(rd => rd.invoice_detail_id === d.id))
  const calculatePrice = (currDetails = getValues('details') as RefundDetail[], includeTax = isRefundTax) => {
    const totalItems = invoice?.invoice_detail.length;
    const deductedPerItem = (discount + rescheduleFee) / totalItems
    let totalPrice: number = currDetails.reduce((acc, curr: RefundDetail) => 
      acc + (curr.price - deductedPerItem), 0
  )
    if (includeTax) {
      totalPrice += invoice.tax_amount
    } 

    if (totalPrice > invoice.total_price) {
      totalPrice = invoice.total_price
      if (!includeTax) {
        totalPrice -= invoice.tax_amount
      }
    }
    setValue('total_price', totalPrice)
  }
  const removeTimestamp = (items: InvoiceDetail[]) => {
    const currentDetails = getValues('details') as RefundDetail[]
    setValue('details', currentDetails.filter(d => !items.find(i => i.id === d.invoice_detail_id)))
    calculatePrice()
  }
  const addItems = (items: InvoiceDetail[]) => {
    const currentDetails = getValues('details') as RefundDetail[]
    const newDetails = items.map(i => ({
      invoice_detail_id: i.id,
      price: i.price
    })) as RefundDetail[]
    setValue('details', [...currentDetails, ...newDetails])
    calculatePrice()
  }
  const handleClickCheckTimestamp = (e, items) => {
    if (e.target.checked) {
      addItems(items)
      return
    }
    removeTimestamp(items)
  }
  const renderField = (detail: InvoiceDetail) => {
    const bookingDetail = detail?.booking_detail
    const courtName = courts.find(c => c.id === bookingDetail?.venue_court_id)?.code ?? 'Field'
    const timeText = `(${hourFormat(bookingDetail.start_time)} - ${hourFormat(bookingDetail.end_time)})`
    return <div key={detail.id} className="j-row p-4 gap-4 items-center">
      <BadmintonCourt />
      <div className="j-col flex-grow">
        {
          bookingDetail ?
            <>
              <div>
                { courtName }
              </div>
              <div className="text-jxs text-grey-dark">
                { timeText }
              </div>
            </> : <></>
        }
      </div>
      <div className="whitespace-nowrap text-jsm">
        {currencyFormat(detail.price)}
      </div>
    </div>
  }
  const renderInvoiceDetailRow = (timestamp: number, items: InvoiceDetail[]) => (
    <div
      key={timestamp}
      className="j-col border border-stroke-subtle rounded"
    >
      <div className="j-row justify-between items-center p-4">
        <div className="j-row justify-start items-center gap-3">
          <input
            name={`refund-check-${timestamp}`}
            type="checkbox"
            className="checkbox"
            checked={isTimestampSelected(items)}
            onChange={(e) => {handleClickCheckTimestamp(e, items)}}
          />
          <div className="test-jsm font-medium">{getLongDateFormat(timestamp, 'id-ID')}</div>
        </div>
        <div className="flex flex-row gap-4 justify-center items-center">
          <div className="text-jsm font-bold whitespace-nowrap">{currencyFormat(getPriceOfDate(items))}</div>
          <div
            aria-hidden
            role="button"
            className={`
              transition-all text-grey-dark
              ${isOpen(timestamp) ? 'rotate-180': ''}
            `}
            onClick={() => {toggleTimestamp(timestamp)}}>
            <RiArrowDownSLine />
          </div>
        </div>
      </div>
      <div
        className={`
          box-border border-t j-col border-stroke-subtle transition-all overflow-hidden duration-0
          ${isOpen(timestamp) ? 'scale-y-100' : 'h-0'}
        `}
      >
        {
          items.map(renderField)
        }
      </div>
    </div>
  )
  const renderInvoiceDetails = (detailsByDate) =>
    <div className="j-col gap-4">
      {
        Object.entries(detailsByDate).map((entry) =>
          renderInvoiceDetailRow(parseInt(entry[0], 10), entry[1] as InvoiceDetail[]))
      }
    </div>
  const handleSelectAll = () => {
    const newDetails = invoice?.invoice_detail.map(i => ({
      invoice_detail_id: i.id,
      price: i.price
    }))

    if (currentRefundDetails && currentRefundDetails.length === newDetails.length) {
      setValue('details', [])
      calculatePrice([])
      return
    }
    
    
    setValue('details', newDetails)
    calculatePrice(newDetails)
  }
  return (
    <div className="j-col gap-4">
      <div className="j-row justify-between items-center">
        <div className="text-jsm text-grey-dark font-normal">Booking Schedule ({Object.keys(invoiceDetailsGroupedByDate).length})</div>
        <Button
          className="text-red-primary flex items-center p-0 hover:cursor-pointer"
          variant='tertierry'
          onClick={handleSelectAll}>
          Select All
        </Button>
      </div>
      <div>
        {renderInvoiceDetails(invoiceDetailsGroupedByDate)}
      </div>
      {/* <div className="text-jsm font-normal text-grey-dark">Invoice Info</div> */}
      <div className="pl-4 pt-0">
        <div
          className="flex w-full text-jsm text-grey-dark font-normal justify-between">
          <div>Sub Total</div>
          <div>{currencyFormat(subTotal)}</div>
        </div>
        { invoice?.booking.rescheduled_from &&
          <div
            className="flex w-full text-jsm text-grey-dark font-normal justify-between">
            <div>Reschedule Fee</div>
            <div>{currencyFormat(rescheduleFee)}</div>
          </div>
        }
        { invoice?.booking.rescheduled_from &&
          <div
            className="flex w-full text-jsm text-grey-dark font-normal justify-between">
            <div>Paid From <span className="text-red-primary">#{invoice?.booking.rescheduled_from.from_custom_id}</span></div>
            <div>-{currencyFormat(paidFromRechedule)}</div>
          </div>
        }
        <div
          className="flex w-full text-jsm text-grey-dark font-normal justify-between horizontal-separator--dash pb-2">
          <div>Discount</div>
          <div>-{currencyFormat(discount ?? 0)}</div>
        </div>
        <div
          className="flex w-full text-jsm text-grey-dark font-normal justify-between py-2 horizontal-separator--dash">
          <div className="flex items-center gap-2">
            <input
              name="isRefundTax"
              type="checkbox"
              className="checkbox"
              checked={isRefundTax}
              onChange={(e) => {
                const newValue = e.target.checked;
                setIsRefundTax(newValue);
                setValue('is_refund_tax', newValue);
                calculatePrice(getValues('details') as RefundDetail[], newValue);
              }}
            />
            Local Tax {venue.tax_fee}%</div>
          <div>{currencyFormat(invoice.tax_amount)}</div>
        </div>
        <div
          className="flex w-full pt-2 text-jsm font-bold justify-between">
          <div>TOTAL INVOICE</div>
          <div>{currencyFormat(invoice.total_price)}</div>
        </div>
        <div className="text-grey-dark text-xs">
          <small>*Reschedule Fee & Discount is non refundable</small>
        </div>
      </div>
    </div>
  );
};

export default RefundDetails;